import React, { useState, useEffect } from 'react';
import { FaDollarSign, FaCog, FaEdit, FaTrash } from 'react-icons/fa';
import { 
  pricingServices, 
  contactInfo, 
  services,
  features,
  petSizes,
  socialLinks,
  googleTag,
  comments,
  reviewLinks
} from '../../data/mockData';
import { Service, ContactInfo, SocialLink, Feature, PetSize, GoogleTag, Comment, ReviewLinks } from '../../types';
import './Admin.scss';

// Type assertion for React Icons
const DollarIcon = FaDollarSign as React.ComponentType<{ className?: string }>;
const CogIcon = FaCog as React.ComponentType<{ className?: string }>;
const EditIcon = FaEdit as React.ComponentType<{ className?: string }>;
const TrashIcon = FaTrash as React.ComponentType<{ className?: string }>;

type TabType = 'pricing' | 'services' | 'contact' | 'features' | 'sizes' | 'social' | 'analytics' | 'comments';

const AdminComplete: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('pricing');
  
  // All data states
  const [pricingData, setPricingData] = useState(pricingServices);
  const [servicesData, setServicesData] = useState<Service[]>(services);
  const [contactData, setContactData] = useState<ContactInfo>(contactInfo);
  const [featuresData, setFeaturesData] = useState<Feature[]>(features);
  const [petSizesData, setPetSizesData] = useState<PetSize[]>(petSizes);
  const [socialLinksData, setSocialLinksData] = useState<SocialLink[]>(socialLinks);
  const [googleTagData, setGoogleTagData] = useState<GoogleTag>(googleTag);
  const [commentsData, setCommentsData] = useState<Comment[]>(comments);
  const [reviewLinksData, setReviewLinksData] = useState<ReviewLinks>(reviewLinks);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(false);
  
  const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT?.replace('/api/send-email', '') || 'https://premium-pets-mail.vercel.app';

  const verifyToken = async (token: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      const data = await response.json();
      if (data.success) {
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('admin_authenticated');
        localStorage.removeItem('admin_token');
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      setIsAuthenticated(false);
      localStorage.removeItem('admin_authenticated');
      localStorage.removeItem('admin_token');
    }
  };

  useEffect(() => {
    const storedAuth = localStorage.getItem('admin_authenticated');
    const storedToken = localStorage.getItem('admin_token');
    if (storedAuth === 'true' && storedToken) {
      // Verify token is still valid
      verifyToken(storedToken);
      loadAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const loadAllData = () => {
    const allData = localStorage.getItem('admin_all_data');
    if (allData) {
      try {
        const parsed = JSON.parse(allData);
        if (parsed.pricingServices) setPricingData(parsed.pricingServices);
        if (parsed.services) setServicesData(parsed.services);
        if (parsed.contactInfo) setContactData(parsed.contactInfo);
        if (parsed.features) setFeaturesData(parsed.features);
        if (parsed.petSizes) setPetSizesData(parsed.petSizes);
        if (parsed.socialLinks) setSocialLinksData(parsed.socialLinks);
        if (parsed.googleTag) setGoogleTagData(parsed.googleTag);
        if (parsed.comments) setCommentsData(parsed.comments);
        if (parsed.reviewLinks) setReviewLinksData(parsed.reviewLinks);
      } catch (e) {
        console.error('Error loading data:', e);
      }
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin-auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          password: password
        })
      });
      
      const data = await response.json();
      
      if (data.success && data.token) {
        setIsAuthenticated(true);
        localStorage.setItem('admin_authenticated', 'true');
        localStorage.setItem('admin_token', data.token);
        loadAllData();
        setEmail('');
        setPassword('');
      } else {
        alert(data.message || 'Incorrect email or password!');
        setPassword('');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please try again.');
      setPassword('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('admin_authenticated');
    localStorage.removeItem('admin_token');
  };

  const handleSave = async () => {
    if (!window.confirm('Are you sure you want to save all changes? This will update the website data.')) {
      return;
    }
    
    setSaveStatus('saving');
    
    try {
      const allData = {
        pricingServices: pricingData,
        services: servicesData,
        contactInfo: contactData,
        features: featuresData,
        petSizes: petSizesData,
        socialLinks: socialLinksData,
        googleTag: googleTagData,
        comments: commentsData,
        reviewLinks: reviewLinksData,
        updatedAt: new Date().toISOString()
      };
      
      localStorage.setItem('admin_all_data', JSON.stringify(allData));
      
      // Save to server for all users
      const token = localStorage.getItem('admin_token');
      if (token) {
        try {
          const saveResponse = await fetch(`${API_BASE_URL}/api/admin-data`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ data: allData })
          });
          
          const saveResult = await saveResponse.json();
          if (!saveResult.success) {
            console.error('Failed to save to server:', saveResult.message);
          }
        } catch (error) {
          console.error('Error saving to server:', error);
        }
      }
      
      // Dispatch custom event to notify other components of data update
      window.dispatchEvent(new CustomEvent('admin-data-updated'));
      
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
      
    } catch (error) {
      console.error('Error saving data:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };

  const handleSaveSection = (sectionName: string) => {
    if (!window.confirm(`Are you sure you want to save changes to ${sectionName}?`)) {
      return;
    }
    
    handleSave();
  };

  // Pricing handlers
  const handlePricingChange = (serviceId: string, size: 'small' | 'medium' | 'large' | 'xlarge', value: string) => {
    const numValue = parseFloat(value) || 0;
    setPricingData(prev =>
      prev.map(service =>
        service.id === serviceId
          ? { ...service, prices: { ...service.prices, [size]: numValue } }
          : service
      )
    );
  };

  // Service handlers
  const handleServiceChange = (serviceId: string, field: keyof Service, value: any) => {
    setServicesData(prev =>
      prev.map(service =>
        service.id === serviceId ? { ...service, [field]: value } : service
      )
    );
  };

  const handleServicePriceChange = (serviceId: string, size: 'small' | 'medium' | 'large' | 'xlarge', value: string) => {
    const numValue = parseFloat(value) || 0;
    setServicesData(prev =>
      prev.map(service =>
        service.id === serviceId
          ? { ...service, price: { ...service.price, [size]: numValue } }
          : service
      )
    );
  };

  // Comments handlers
  const handleAddComment = () => {
    const newComment: Comment = {
      id: `comment-${Date.now()}`,
      name: '',
      photoUrl: '',
      text: '',
      rating: 5,
      date: new Date().toISOString(),
      platform: 'google',
      commentPhotos: []
    };
    setCommentsData(prev => [...prev, newComment]);
    setEditingCommentId(newComment.id);
  };

  const handleCommentChange = (commentId: string, field: keyof Comment, value: any) => {
    setCommentsData(prev =>
      prev.map(comment =>
        comment.id === commentId ? { ...comment, [field]: value } : comment
      )
    );
  };

  const handleDeleteComment = (commentId: string) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      setCommentsData(prev => prev.filter(comment => comment.id !== commentId));
      if (editingCommentId === commentId) {
        setEditingCommentId(null);
      }
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <div className="login-form-section">
          <div className="login-container">
            <h2>Admin Panel</h2>
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label htmlFor="email">Email:</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  autoFocus
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password:</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
              </div>
              <button type="submit" className="btn-primary" disabled={isLoading}>
                {isLoading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>
          </div>
        </div>
        <div className="login-hero-section">
          <div 
            className="login-image-bg"
            style={{
              backgroundImage: `linear-gradient(135deg, rgba(7, 35, 77, 0.85) 0%, rgba(12, 63, 140, 0.75) 100%), url('/assets/images/photo-caring.webp')`
            }}
          >
          <div className="hero-content">
            <div className="hero-logo">
              <img 
                src="/assets/images/logo.svg" 
                alt="Premium Pets Logo" 
                className="logo-image"
              />
            </div>
            <h1 className="hero-title">
              <span className="hero-title-line">Welcome to</span>
              <span className="hero-title-line highlight">Premium Pets</span>
              <span className="hero-title-line">Admin Panel</span>
            </h1>
            <p className="hero-subtitle">
              Manage your website content, prices, and settings with ease.
            </p>
            <div className="hero-feature-buttons">
              <button className="btn-outline" aria-label="Update pricing information">
                <DollarIcon className="btn-icon" aria-hidden="true" />
                Update Pricing
              </button>
              <button className="btn-outline" aria-label="Manage services">
                <CogIcon className="btn-icon" aria-hidden="true" />
                Manage Services
              </button>
              <button className="btn-outline" aria-label="Edit content">
                <EditIcon className="btn-icon" aria-hidden="true" />
                Edit Content
              </button>
            </div>
          </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div className="header-left">
          <img 
            src="/assets/images/logo.svg" 
            alt="Premium Pets Logo" 
            className="header-logo"
          />
          <h1>Admin Panel</h1>
        </div>
        <button onClick={handleLogout} className="btn-logout">Logout</button>
      </div>

      <div className="admin-tabs">
        <button 
          className={activeTab === 'pricing' ? 'active' : ''} 
          onClick={() => setActiveTab('pricing')}
        >
          Pricing
        </button>
        <button 
          className={activeTab === 'services' ? 'active' : ''} 
          onClick={() => setActiveTab('services')}
        >
          Services
        </button>
        <button 
          className={activeTab === 'contact' ? 'active' : ''} 
          onClick={() => setActiveTab('contact')}
        >
          Contact
        </button>
        <button 
          className={activeTab === 'features' ? 'active' : ''} 
          onClick={() => setActiveTab('features')}
        >
          Features
        </button>
        <button 
          className={activeTab === 'sizes' ? 'active' : ''} 
          onClick={() => setActiveTab('sizes')}
        >
          Sizes
        </button>
        <button 
          className={activeTab === 'social' ? 'active' : ''} 
          onClick={() => setActiveTab('social')}
        >
          Social Links
        </button>
        <button 
          className={activeTab === 'analytics' ? 'active' : ''} 
          onClick={() => setActiveTab('analytics')}
        >
          Google Analytics
        </button>
        <button 
          className={activeTab === 'comments' ? 'active' : ''} 
          onClick={() => setActiveTab('comments')}
        >
          Comments
        </button>
      </div>

      <div className="admin-content">
        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <section className="admin-section">
            <h2>Service Pricing</h2>
            <div className="pricing-table">
              <table>
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Small</th>
                    <th>Medium</th>
                    <th>Large</th>
                    <th>XL</th>
                  </tr>
                </thead>
                <tbody>
                  {pricingData.map((service) => (
                    <tr key={service.id}>
                      <td className="service-name">{service.name}</td>
                      <td>
                        <input
                          type="number"
                          value={service.prices.small}
                          onChange={(e) => handlePricingChange(service.id, 'small', e.target.value)}
                          min="0"
                          step="1"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={service.prices.medium}
                          onChange={(e) => handlePricingChange(service.id, 'medium', e.target.value)}
                          min="0"
                          step="1"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={service.prices.large}
                          onChange={(e) => handlePricingChange(service.id, 'large', e.target.value)}
                          min="0"
                          step="1"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={service.prices.xlarge}
                          onChange={(e) => handlePricingChange(service.id, 'xlarge', e.target.value)}
                          min="0"
                          step="1"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="section-actions">
              <button onClick={() => handleSaveSection('Pricing')} className="btn-save-section" disabled={saveStatus === 'saving'}>
                {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? '✓ Saved!' : 'Save Pricing'}
              </button>
            </div>
          </section>
        )}

        {/* Services Tab - Full service editing */}
        {activeTab === 'services' && (
          <section className="admin-section">
            <h2>Main Services</h2>
            {servicesData.map((service, index) => (
              <div key={service.id} className="service-editor">
                <h3>Service {index + 1}: {service.name}</h3>
                <div className="form-group">
                  <label>Name:</label>
                  <input
                    type="text"
                    value={service.name}
                    onChange={(e) => handleServiceChange(service.id, 'name', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Description:</label>
                  <textarea
                    value={service.description}
                    onChange={(e) => handleServiceChange(service.id, 'description', e.target.value)}
                    rows={3}
                  />
                </div>
                <div className="form-group">
                  <label>Prices:</label>
                  <div className="price-inputs">
                    <input type="number" value={service.price.small} onChange={(e) => handleServicePriceChange(service.id, 'small', e.target.value)} placeholder="Small" />
                    <input type="number" value={service.price.medium} onChange={(e) => handleServicePriceChange(service.id, 'medium', e.target.value)} placeholder="Medium" />
                    <input type="number" value={service.price.large} onChange={(e) => handleServicePriceChange(service.id, 'large', e.target.value)} placeholder="Large" />
                    <input type="number" value={service.price.xlarge} onChange={(e) => handleServicePriceChange(service.id, 'xlarge', e.target.value)} placeholder="XL" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Features (uma por linha):</label>
                  <textarea
                    value={service.features.join('\n')}
                    onChange={(e) => handleServiceChange(service.id, 'features', e.target.value.split('\n').filter(f => f.trim()))}
                    rows={8}
                  />
                </div>
              </div>
            ))}
            <div className="section-actions">
              <button onClick={() => handleSaveSection('Services')} className="btn-save-section" disabled={saveStatus === 'saving'}>
                {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? '✓ Saved!' : 'Save Services'}
              </button>
            </div>
          </section>
        )}

        {/* Contact Tab */}
        {activeTab === 'contact' && (
          <section className="admin-section">
            <h2>Contact Information</h2>
            <div className="service-editor">
              <div className="form-group">
                <label>Phone:</label>
                <input
                  type="text"
                  value={contactData.phone}
                  onChange={(e) => setContactData(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  value={contactData.email}
                  onChange={(e) => setContactData(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Address:</label>
                <input
                  type="text"
                  value={contactData.address}
                  onChange={(e) => setContactData(prev => ({ ...prev, address: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Business Hours (Weekdays):</label>
                <input
                  type="text"
                  value={contactData.hours}
                  onChange={(e) => setContactData(prev => ({ ...prev, hours: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Business Hours (Weekend):</label>
                <input
                  type="text"
                  value={contactData.hoursWeekend}
                  onChange={(e) => setContactData(prev => ({ ...prev, hoursWeekend: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Service Area:</label>
                <input
                  type="text"
                  value={contactData.serviceArea}
                  onChange={(e) => setContactData(prev => ({ ...prev, serviceArea: e.target.value }))}
                />
              </div>
            </div>
            <div className="section-actions">
              <button onClick={() => handleSaveSection('Contact')} className="btn-save-section" disabled={saveStatus === 'saving'}>
                {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? '✓ Saved!' : 'Save Contact'}
              </button>
            </div>
          </section>
        )}

        {/* Features Tab */}
        {activeTab === 'features' && (
          <section className="admin-section">
            <h2>Features</h2>
            {featuresData.map((feature, index) => (
              <div key={feature.id} className="feature-editor">
                <h3>Feature {index + 1}</h3>
                <div className="form-group">
                  <label>Title:</label>
                  <input
                    type="text"
                    value={feature.title}
                    onChange={(e) => setFeaturesData(prev => prev.map(f => f.id === feature.id ? { ...f, title: e.target.value } : f))}
                  />
                </div>
                <div className="form-group">
                  <label>Description:</label>
                  <textarea
                    value={feature.description}
                    onChange={(e) => setFeaturesData(prev => prev.map(f => f.id === feature.id ? { ...f, description: e.target.value } : f))}
                    rows={2}
                  />
                </div>
              </div>
            ))}
            <div className="section-actions">
              <button onClick={() => handleSaveSection('Features')} className="btn-save-section" disabled={saveStatus === 'saving'}>
                {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? '✓ Saved!' : 'Save Features'}
              </button>
            </div>
          </section>
        )}

        {/* Sizes Tab */}
        {activeTab === 'sizes' && (
          <section className="admin-section">
            <h2>Pet Sizes</h2>
            {petSizesData.map((size, index) => (
              <div key={size.id} className="size-editor">
                <h3>{size.name}</h3>
                <div className="form-group">
                  <label>Name:</label>
                  <input
                    type="text"
                    value={size.name}
                    onChange={(e) => setPetSizesData(prev => prev.map(s => s.id === size.id ? { ...s, name: e.target.value } : s))}
                  />
                </div>
                <div className="form-group">
                  <label>Weight:</label>
                  <input
                    type="text"
                    value={size.weight}
                    onChange={(e) => setPetSizesData(prev => prev.map(s => s.id === size.id ? { ...s, weight: e.target.value } : s))}
                  />
                </div>
              </div>
            ))}
            <div className="section-actions">
              <button onClick={() => handleSaveSection('Sizes')} className="btn-save-section" disabled={saveStatus === 'saving'}>
                {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? '✓ Saved!' : 'Save Sizes'}
              </button>
            </div>
          </section>
        )}

        {/* Social Links Tab */}
        {activeTab === 'social' && (
          <section className="admin-section">
            <h2>Social Links</h2>
            {socialLinksData.map((social, index) => (
              <div key={index} className="social-editor">
                <h3>{social.name}</h3>
                <div className="form-group">
                  <label>Name:</label>
                  <input
                    type="text"
                    value={social.name}
                    onChange={(e) => setSocialLinksData(prev => prev.map((s, i) => i === index ? { ...s, name: e.target.value } : s))}
                  />
                </div>
                <div className="form-group">
                  <label>URL:</label>
                  <input
                    type="url"
                    value={social.url}
                    onChange={(e) => setSocialLinksData(prev => prev.map((s, i) => i === index ? { ...s, url: e.target.value } : s))}
                  />
                </div>
              </div>
            ))}
            <div className="section-actions">
              <button onClick={() => handleSaveSection('Social Links')} className="btn-save-section" disabled={saveStatus === 'saving'}>
                {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? '✓ Saved!' : 'Save Social Links'}
              </button>
            </div>
          </section>
        )}

        {/* Google Analytics Tab */}
        {activeTab === 'analytics' && (
          <section className="admin-section">
            <h2>Google Analytics / Google Tag Manager</h2>
            <div className="service-editor">
              <div className="form-group">
                <label>Script Source URL:</label>
                <input
                  type="text"
                  value={googleTagData.scriptSrc}
                  onChange={(e) => setGoogleTagData(prev => ({ ...prev, scriptSrc: e.target.value }))}
                  placeholder="https://www.googletagmanager.com/gtag/js?id=AW-XXXXX"
                />
              </div>
              <div className="form-group">
                <label>Configuration Script:</label>
                <textarea
                  value={googleTagData.inlineScript}
                  onChange={(e) => setGoogleTagData(prev => ({ ...prev, inlineScript: e.target.value }))}
                  rows={8}
                  placeholder={`window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'AW-XXXXX');`}
                />
              </div>
            </div>
            <div className="section-actions">
              <button onClick={() => handleSaveSection('Google Analytics')} className="btn-save-section" disabled={saveStatus === 'saving'}>
                {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? '✓ Saved!' : 'Save Google Analytics'}
              </button>
            </div>
          </section>
        )}

        {/* Comments Tab */}
        {activeTab === 'comments' && (
          <section className="admin-section">
            <h2>Review Links</h2>
            <div className="service-editor">
              <div className="form-group">
                <label>Google Review URL:</label>
                <input
                  type="text"
                  value={reviewLinksData.googleUrl}
                  onChange={(e) => setReviewLinksData(prev => ({ ...prev, googleUrl: e.target.value }))}
                  placeholder="https://g.page/r/YOUR_GOOGLE_REVIEW_LINK"
                />
              </div>
              <div className="form-group">
                <label>Nextdoor Review URL:</label>
                <input
                  type="text"
                  value={reviewLinksData.nextdoorUrl}
                  onChange={(e) => setReviewLinksData(prev => ({ ...prev, nextdoorUrl: e.target.value }))}
                  placeholder="https://nextdoor.com/pages/premium-pets-mobile-grooming/"
                />
              </div>
            </div>
            <div className="section-actions">
              <button onClick={() => handleSaveSection('Review Links')} className="btn-save-section" disabled={saveStatus === 'saving'}>
                {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? '✓ Saved!' : 'Save Review Links'}
              </button>
            </div>

            <h2 style={{ marginTop: '3rem' }}>Comments / Reviews</h2>
            <div className="section-actions" style={{ marginBottom: '1rem', borderTop: 'none', paddingTop: 0 }}>
              <button onClick={handleAddComment} className="btn-primary">
                Add New Comment
              </button>
            </div>
            {commentsData.map((comment) => (
              <div key={comment.id} className="service-editor" style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3>Comment: {comment.name || 'New Comment'}</h3>
                  <button 
                    onClick={() => handleDeleteComment(comment.id)} 
                    className="btn-delete-icon"
                    aria-label="Delete comment"
                    title="Delete comment"
                  >
                    <TrashIcon />
                  </button>
                </div>
                <div className="form-group">
                  <label>Name:</label>
                  <input
                    type="text"
                    value={comment.name}
                    onChange={(e) => handleCommentChange(comment.id, 'name', e.target.value)}
                    placeholder="Customer Name"
                  />
                </div>
                <div className="form-group">
                  <label>Photo URL:</label>
                  <input
                    type="text"
                    value={comment.photoUrl}
                    onChange={(e) => handleCommentChange(comment.id, 'photoUrl', e.target.value)}
                    placeholder="https://example.com/photo.jpg"
                  />
                </div>
                <div className="form-group">
                  <label>Comment Text:</label>
                  <textarea
                    value={comment.text}
                    onChange={(e) => handleCommentChange(comment.id, 'text', e.target.value)}
                    rows={4}
                    placeholder="Customer review text..."
                  />
                </div>
                <div className="form-group">
                  <label>Rating (1-5):</label>
                  <select
                    value={comment.rating}
                    onChange={(e) => handleCommentChange(comment.id, 'rating', parseInt(e.target.value))}
                  >
                    <option value={1}>1 Star</option>
                    <option value={2}>2 Stars</option>
                    <option value={3}>3 Stars</option>
                    <option value={4}>4 Stars</option>
                    <option value={5}>5 Stars</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Platform:</label>
                  <select
                    value={comment.platform}
                    onChange={(e) => handleCommentChange(comment.id, 'platform', e.target.value)}
                  >
                    <option value="google">Google</option>
                    <option value="nextdoor">Nextdoor</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date:</label>
                  <input
                    type="date"
                    value={comment.date.split('T')[0]}
                    onChange={(e) => handleCommentChange(comment.id, 'date', new Date(e.target.value).toISOString())}
                  />
                </div>
                <div className="form-group">
                  <label>Comment Photos (up to 3 URLs):</label>
                  {[0, 1, 2].map((index) => (
                    <input
                      key={index}
                      type="text"
                      value={comment.commentPhotos?.[index] || ''}
                      onChange={(e) => {
                        const currentPhotos = comment.commentPhotos || [];
                        const newPhotos = [...currentPhotos];
                        if (e.target.value.trim()) {
                          newPhotos[index] = e.target.value.trim();
                        } else {
                          newPhotos.splice(index, 1);
                        }
                        // Remove empty strings and keep only first 3
                        const filteredPhotos = newPhotos.filter(url => url.trim()).slice(0, 3);
                        handleCommentChange(comment.id, 'commentPhotos', filteredPhotos);
                      }}
                      placeholder={`Photo ${index + 1} URL (optional)`}
                      style={{ marginBottom: '0.5rem' }}
                    />
                  ))}
                </div>
              </div>
            ))}
            <div className="section-actions">
              <button onClick={() => handleSaveSection('Comments')} className="btn-save-section" disabled={saveStatus === 'saving'}>
                {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? '✓ Saved!' : 'Save Comments'}
              </button>
            </div>
          </section>
        )}

      </div>

    </div>
  );
};

export default AdminComplete;

